import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, User, Calendar, Shield, Clock, Target, Upload, Bell, Plus, Search, Eye, FileText, MessageCircle, UserPlus, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Footer } from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";

export default function HealthcareProfessionals() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(1); // New state for active tab

  const scrollLogosBy = (direction: 'prev' | 'next') => {
    const container = logoContainerRef.current;
    if (!container) return;
    const amount = container.clientWidth * 0.3; // scroll by ~30% of visible width for smoother movement
    container.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };
  const handlePrevLogo = () => {
    setCurrentLogoIndex((i) => (i - 1 + 10) % 10);
    scrollLogosBy('prev');
  };
  const handleNextLogo = () => {
    setCurrentLogoIndex((i) => (i + 1) % 10);
    scrollLogosBy('next');
  };

  useSEO({
    title: "Healthcare Professionals - Nomera Health",
    description: "Find your next healthcare job. Set your preferences once and let hospitals come to you. No more endless applications.",
    keywords: "healthcare jobs, nursing jobs, medical careers, healthcare recruitment, travel nursing, healthcare staffing, Missouri, Kansas"
  });

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Ambient background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Header */}
      <header role="banner" className="sticky top-0 z-50">
        <div
          className={`relative backdrop-blur-md transition-all duration-300 ${
            scrolled
              ? 'shadow-[0_6px_20px_rgba(0,0,0,.06)]'
              : ''
          }`}
          style={{ backdropFilter: 'blur(8px)', backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.75)' }}
        >
          <style>{`
            @keyframes nxr-logo-in { from { opacity:0; transform: scale(.98) } to { opacity:1; transform: scale(1) } }
            @keyframes nxr-logo-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.01) } }
            .nxr-logo { animation: nxr-logo-in .6s ease both, nxr-logo-breathe 6s ease-in-out infinite; }
            .nxr-navlink { position: relative; transition: color .2s ease; }
            .nxr-navlink:after { content:''; position:absolute; left:0; right:0; bottom:-4px; height:2px; background: linear-gradient(90deg, rgba(163,207,162,.6), rgba(155,143,193,.6)); transform: scaleX(0); transform-origin:left; transition: transform .25s ease, opacity .25s ease; opacity:0; }
            .nxr-navlink:hover:after, .nxr-navlink:focus-visible:after { transform: scaleX(1); opacity:1; }
            @media(prefers-reduced-motion:reduce){ .nxr-logo { animation:none !important } .nxr-navlink:after { transition: none } }
          `}</style>
          
          <div className="max-w-6xl mx-auto px-6 h-16 md:h-18 flex items-center justify-between text-neutral-800">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
              <img
                src="/lovable-uploads/Favicon.png"
                alt="Nomera Health Logo" 
                className="w-8 h-8 rounded-xl nxr-logo"
              />
              <span className="text-lg font-bold">Nomera Health</span>
            </Link>

            {/* Center: Desktop Nav */}
            <nav className="hidden md:block" aria-label="Main">
              <ul className="flex items-center gap-6">
                <li><Link to="/" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Home</Link></li>
                <li><Link to="/employers" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Employers</Link></li>
                <li><Link to="/vision" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
              </ul>
            </nav>

            {/* Right: Secondary + CTA */}
            <div className="hidden md:flex items-center gap-3">
              <span className="text-neutral-400 font-semibold pointer-events-none select-none">Coming soon</span>
              <Button
                size="sm"
                disabled
                className="rounded-xl bg-[#A3CFA2]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none"
              >
                Coming soon
              </Button>
            </div>

            {/* Mobile: hamburger */}
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setIsMenuOpen((v) => !v)}
              className="md:hidden p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile panel */}
          <div
            id="mobile-menu"
            className={`md:hidden origin-top transition-[max-height,opacity] duration-300 ${isMenuOpen ? 'max-h-[360px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
          >
            <div className="px-6 pb-6 pt-2">
              <div className="rounded-xl border border-black/5 bg-white/80 supports-[backdrop-filter]:backdrop-blur-md p-4">
                <nav aria-label="Main mobile">
                  <ul className="flex flex-col gap-3">
                    <li><Link to="/" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Home</Link></li>
                    <li><Link to="/employers" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Employers</Link></li>
                    <li><Link to="/vision" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
                  </ul>
                </nav>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-neutral-400 font-semibold">Coming soon</span>
                  <Button
                    size="sm"
                    disabled
                    className="w-full rounded-xl bg-[#A3CFA2]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none"
                  >
                    Coming soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative min-h-[80vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #FAF6F0 0%, #F0F8F0 50%, #FFFFFF 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="max-w-2xl">
              {/* Header */}
              <div className="mb-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-neutral-900">
                  <span className="block text-[#A3CFA2]">Find your dream job</span>
                  <span className="block">in just a few <span className="text-[#9B8FC1]">clicks.</span></span>
                </h1>
              </div>

              {/* Subheader */}
              <div className="mb-4">
                <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-neutral-600 leading-relaxed">
                  No more repetitive applications. Take control of your career by setting your pay, schedule, and location preferences.
                </p>
              </div>

              {/* Trust Points */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#A3CFA2] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">Stress-free job hunt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#A3CFA2] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">Say goodbye to the long wait</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#A3CFA2] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">Hospitals compete for you</span>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-8">
                <Button
                  size="lg"
                  disabled
                  className="bg-[#A3CFA2]/40 text-white/80 font-bold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 shadow-none pointer-events-none select-none"
                >
                  Coming soon
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  disabled
                  className="border-2 border-neutral-200 text-neutral-400 font-semibold text-base sm:text-lg px-5 sm:px-6 py-3 sm:py-4 pointer-events-none select-none"
                >
                  Coming soon
                </Button>
              </div>
            </div>

            {/* Right: Nurse Image with Artistic Framing */}
            <div className="relative lg:flex justify-center items-center">
              {/* Main Image Container */}
              <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-4xl mx-auto lg:mx-0">
                {/* Abstract Background Shapes */}
                <div className="absolute inset-0 -z-10">
                  {/* Large circular element with stripes */}
                  <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 lg:-bottom-8 lg:-right-8 w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-[#A3CFA2]/20 to-[#9B8FC1]/20 rounded-full opacity-80">
                    <div className="absolute inset-2 bg-white/30 rounded-full"></div>
                    <div className="absolute inset-0 rounded-full" style={{
                      background: `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(163,207,162,0.3) 4px, rgba(163,207,162,0.3) 8px)`
                    }}></div>
                  </div>
                  
                  {/* Floating diamond shape */}
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 lg:-top-4 lg:-right-4 w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#9B8FC1]/30 to-[#A3CFA2]/30 transform rotate-45 opacity-80"></div>
                  
                  {/* Curved abstract shape */}
                  <div className="absolute -bottom-2 -left-2 sm:-bottom-3 sm:-left-3 lg:-bottom-4 lg:-left-4 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-[#A3CFA2]/20 to-[#9B8FC1]/20 rounded-full opacity-60" style={{
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 70%)'
                  }}></div>
                  
                  {/* Dot pattern */}
                  <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 lg:-bottom-2 lg:-right-2 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 opacity-60">
                    <div className="grid grid-cols-4 gap-0.5 sm:gap-1">
                      {Array.from({ length: 16 }).map((_, i) => (
                        <div key={i} className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-[#9B8FC1] rounded-full opacity-60"></div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Nurse Image Container */}
                <div className="relative bg-gradient-to-br from-[#A3CFA2]/10 to-[#9B8FC1]/10 rounded-2xl sm:rounded-3xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-xl lg:shadow-2xl">
                  {/* Speech bubble elements */}
                  <div className="absolute -top-4 -left-2 sm:-top-6 sm:-left-3 lg:-top-8 lg:-left-4 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 py-1 sm:px-3 sm:py-2 shadow-lg border border-[#A3CFA2]/20">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#A3CFA2]" />
                      <span className="text-xs sm:text-sm font-medium text-[#9B8FC1]">Great Pay</span>
                    </div>
                  </div>
                  
                  <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-3 lg:-top-6 lg:-right-4 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 py-1 sm:px-3 sm:py-2 shadow-lg border border-[#9B8FC1]/20">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#9B8FC1]" />
                      <span className="text-xs sm:text-sm font-medium text-[#A3CFA2]">Flexible Schedule</span>
                    </div>
                  </div>

                  {/* Main Nurse Image */}
                  <div className="relative">
                    <img
                      src="/materials/happy-nurse-and-ipad.png"
                      alt="Happy healthcare professional with iPad"
                      className="w-full h-auto rounded-xl sm:rounded-2xl shadow-lg object-cover"
                    />
                    
                    {/* Subtle overlay for depth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent rounded-xl sm:rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Jobs by City (replaces partner logos section here) */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
              Access <span className="text-[#9B8FC1]">hidden opportunities</span>
            </h2>
            <p className="text-neutral-600 mt-2">Across Missouri and Kansas</p>
          </div>

          {/* Local styles for marquee (mirrors landing page speed/feel) */}
          <style>{`
            .jobs-marquee {
              position: relative;
              overflow: hidden;
              padding: 0.75rem 0;
              -webkit-mask-image: linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%);
                      mask-image: linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%);
            }
                         .jobs-track {
               display: flex;
               align-items: stretch;
               gap: clamp(16px, 3vw, 24px);
               width: max-content;
               list-style: none;
               padding: 0;
               margin: 0;
               animation: jobs-scroll 60s linear infinite;
               will-change: transform;
             }
             .jobs-track--reverse { animation-direction: reverse; }
             .jobs-marquee:hover .jobs-track { animation-play-state: paused; }
             @keyframes jobs-scroll { from { transform: translateX(0) } to { transform: translateX(-50%) } }
             .jobs-card { min-width: clamp(240px, 38vw, 320px); }
             @media (max-width: 768px) { .jobs-track { gap: 16px; animation-duration: 47s } }
             @media (max-width: 480px) { .jobs-track { gap: 12px; animation-duration: 41s } }
          `}</style>

          {/* Row 1 */}
          <div className="jobs-marquee" aria-label="Featured jobs across Missouri and Kansas – row 1">
            <ul className="jobs-track">
              {(() => {
                const jobs = [
                  { city: 'Kansas City, MO', pay: '$3,120/wk', role: 'RN — Med Surg', shift: 'Days, 3x12' },
                  { city: 'St. Louis, MO', pay: '$3,040/wk', role: 'LPN', shift: 'Days, 4x10' },
                  { city: 'Columbia, MO', pay: '$2,980/wk', role: 'CNA', shift: 'Days, 4x10' },
                  { city: 'Springfield, MO', pay: '$2,920/wk', role: 'MA', shift: 'Nights, 3x12' },
                  { city: 'Independence, MO', pay: '$2,880/wk', role: 'Phlebotomist', shift: 'Days, 5x8' },
                  { city: 'Overland Park, KS', pay: '$3,050/wk', role: 'PCT', shift: 'Nights, 3x12' },
                  { city: 'Lenexa, KS', pay: '$2,940/wk', role: 'NP', shift: 'Nights, 3x12' },
                  { city: 'Olathe, KS', pay: '$2,900/wk', role: 'PT', shift: 'Days, 3x12' },
                  { city: 'Topeka, KS', pay: '$2,860/wk', role: 'OT', shift: 'Days, 3x12' },
                ];
                const loop = [...jobs, ...jobs];
                return loop.map((job, idx) => (
                  <li key={`r1-${idx}`}>
                    <div className="jobs-card rounded-2xl border border-neutral-200 bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 p-4">
                      <div className="flex items-start justify-between">
                        <h3 className="text-[15px] sm:text-base font-semibold text-neutral-900">{job.city}</h3>
                        <span className="text-[13px] sm:text-sm font-semibold text-neutral-900">{job.pay}</span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-600">
                        <div className="font-semibold text-neutral-800">{job.role}</div>
                        <div className="mt-1">{job.shift}</div>
                      </div>
                    </div>
                  </li>
                ));
              })()}
            </ul>
          </div>

          {/* Row 2 (opposite direction) */}
          <div className="jobs-marquee mt-4" aria-label="Featured jobs across Missouri and Kansas – row 2">
            <ul className="jobs-track jobs-track--reverse">
              {(() => {
                const jobs = [
                  { city: 'Joplin, MO', pay: '$2,820/wk', role: 'Respiratory Therapist', shift: 'Days, 4x10' },
                  { city: 'Jefferson City, MO', pay: '$2,890/wk', role: 'Radiologic Technologist', shift: 'Nights, 3x12' },
                  { city: 'Blue Springs, MO', pay: '$2,870/wk', role: 'Medical Technologist', shift: 'Days, 5x8' },
                  { city: 'Lee\'s Summit, MO', pay: '$2,910/wk', role: 'Pharmacy Technician', shift: 'Nights, 3x12' },
                  { city: 'Wichita, KS', pay: '$3,000/wk', role: 'Front Desk/Medical Receptionist', shift: 'Days, 3x12' },
                  { city: 'Lawrence, KS', pay: '$2,880/wk', role: 'Surgical Technologist', shift: 'Days, 3x12' },
                  { city: 'Manhattan, KS', pay: '$2,840/wk', role: 'Cath Lab Technologist', shift: 'Days, 5x8' },
                  { city: 'Shawnee, KS', pay: '$2,910/wk', role: 'Emergency RN', shift: 'Days, 3x12' },
                  { city: 'Hutchinson, KS', pay: '$2,830/wk', role: 'ICU RN', shift: 'Days, 5x8' },
                ];
                const loop = [...jobs, ...jobs];
                return loop.map((job, idx) => (
                  <li key={`r2-${idx}`}>
                    <div className="jobs-card rounded-2xl border border-neutral-200 bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 p-4">
                      <div className="flex items-start justify-between">
                        <h3 className="text-[15px] sm:text-base font-semibold text-neutral-900">{job.city}</h3>
                        <span className="text-[13px] sm:text-sm font-semibold text-neutral-900">{job.pay}</span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-600">
                        <div className="font-semibold text-neutral-800">{job.role}</div>
                        <div className="mt-1">{job.shift}</div>
                      </div>
                    </div>
                  </li>
                ));
              })()}
            </ul>
          </div>

          <div className="text-center mt-8">
            <Button disabled aria-disabled="true" className="bg-[#A3CFA2] text-white font-semibold px-6 py-3 shadow-lg opacity-50 cursor-not-allowed">Search Jobs</Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Three simple steps to find your next healthcare opportunity
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="relative group">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-100">
                {/* Placeholder Image Container */}
                <div className="relative mb-6">
                  <div className="w-full h-48 rounded-xl overflow-hidden">
                    <img
                      src="/materials/profile-art-two-iphone-carry.png"
                      alt="Healthcare professional creating profile on iPhone"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-neutral-900 mb-3 text-left">1. Create Your Profile</h3>
                <p className="text-neutral-600 text-left leading-relaxed">
                  Set your preferences for pay, schedule, location, and specialty. Upload your credentials and experience.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="relative group">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-100">
                {/* Placeholder Image Container */}
                <div className="relative mb-6">
                  <div className="w-full h-48 rounded-xl overflow-hidden">
                    <img
                      src="/materials/data-based-matching-image.png"
                      alt="Data-based job matching for healthcare professionals"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-neutral-900 mb-3 text-left">2. Get Matched</h3>
                <p className="text-neutral-600 text-left leading-relaxed">
                  Our AI matches you with relevant job opportunities based on your preferences and qualifications.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="relative group">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-100">
                {/* Placeholder Image Container */}
                <div className="relative mb-6">
                  <div className="w-full h-48 rounded-xl overflow-hidden">
                    <img
                      src="/materials/interview-image-talking-table.png"
                      alt="Healthcare professional in interview discussion at table"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-neutral-900 mb-3 text-left">3. Interview & Choose</h3>
                <p className="text-neutral-600 text-left leading-relaxed">
                  Choose interviews that fit your goals. Then, with more job offers to work with, go into your next job with confidence, clarity, and more control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Why Choose Nomera Health?
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Interactive Feature Tabs */}
            <div className="space-y-6">
              {/* Tab 1 - Transparency */}
              <div 
                className={`group cursor-pointer transition-all duration-300 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl border-2 ${
                  activeTab === 1 
                    ? 'border-[#A3CFA2] shadow-xl scale-[1.02]' 
                    : 'border-transparent hover:border-[#A3CFA2]/20'
                }`}
                onClick={() => setActiveTab(1)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    activeTab === 1 ? 'bg-[#A3CFA2] scale-110' : 'bg-[#A3CFA2]/20 group-hover:bg-[#A3CFA2]/40'
                  }`}>
                    <Shield className={`w-6 h-6 transition-colors duration-300 ${
                      activeTab === 1 ? 'text-white' : 'text-[#A3CFA2]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      activeTab === 1 ? 'text-[#A3CFA2]' : 'text-neutral-900 group-hover:text-[#A3CFA2]'
                    }`}>
                      Transparency at every step
                    </h3>
                    {activeTab === 1 && (
                      <p className="text-neutral-600 leading-relaxed animate-fade-in">
                        No more guessing if anyone saw your application. With Nomera Health, you're notified when there's a match, when interviews are available, and when offers come in. You stay in the driver's seat from start to finish.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab 2 - Value Recognition */}
              <div 
                className={`group cursor-pointer transition-all duration-300 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl border-2 ${
                  activeTab === 2 
                    ? 'border-[#9B8FC1] shadow-xl scale-[1.02]' 
                    : 'border-transparent hover:border-[#9B8FC1]/20'
                }`}
                onClick={() => setActiveTab(2)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    activeTab === 2 ? 'bg-[#9B8FC1] scale-110' : 'bg-[#9B8FC1]/20 group-hover:bg-[#9B8FC1]/40'
                  }`}>
                    <Target className={`w-6 h-6 transition-colors duration-300 ${
                      activeTab === 2 ? 'text-white' : 'text-[#9B8FC1]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      activeTab === 2 ? 'text-[#9B8FC1]' : 'text-neutral-700 group-hover:text-[#9B8FC1]'
                    }`}>
                      We recognize your true value
                    </h3>
                    {activeTab === 2 && (
                      <p className="text-neutral-600 leading-relaxed animate-fade-in">
                        Healthcare professionals are the backbone of the system. Long shifts, little sleep, and endless responsibilities—you hold it all together. That's why our platform is built to respect your time and energy. Instead of filling out dozens of repetitive applications, let hospitals come to you.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab 3 - Growing Movement */}
              <div 
                className={`group cursor-pointer transition-all duration-300 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl border-2 ${
                  activeTab === 3 
                    ? 'border-[#A3CFA2] shadow-xl scale-[1.02]' 
                    : 'border-transparent hover:border-[#A3CFA2]/20'
                }`}
                onClick={() => setActiveTab(3)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    activeTab === 3 ? 'bg-[#A3CFA2] scale-110' : 'bg-[#A3CFA2]/20 group-hover:bg-[#A3CFA2]/40'
                  }`}>
                    <Clock className={`w-6 h-6 transition-colors duration-300 ${
                      activeTab === 3 ? 'text-white' : 'text-[#A3CFA2]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      activeTab === 3 ? 'text-[#A3CFA2]' : 'text-neutral-700 group-hover:text-[#A3CFA2]'
                    }`}>
                      Be part of a growing movement
                    </h3>
                    {activeTab === 3 && (
                      <p className="text-neutral-600 leading-relaxed animate-fade-in">
                        We're building Nomera Health with you in mind and your feedback shapes the future. From mock interviews to industry insights, we're creating tools to support your career growth, not just your next job search.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Interactive Content Area */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 h-[600px] shadow-lg border border-neutral-100 flex items-center justify-center">
                {/* Tab 1 Content */}
                {activeTab === 1 && (
                  <div className="text-center text-neutral-700 animate-fade-in w-full h-full flex flex-col items-center justify-center p-4">
                    <div className="relative mb-6">
                      <img 
                        src="/materials/transparency-every-stage-image.png" 
                        alt="Transparency at every stage - showing clear communication and visibility throughout the process"
                        className="w-52 h-52 md:w-64 md:h-64 lg:w-80 lg:h-80 object-contain rounded-xl drop-shadow-lg"
                      />
                      {/* Floating notification badges for visual appeal */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#A3CFA2] rounded-full flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-white text-xs font-bold">3</span>
                      </div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-[#9B8FC1] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-3">Stay Informed</h3>
                    <p className="text-neutral-600 leading-relaxed max-w-xs">
                      Real-time notifications keep you updated on every step of your application process. No more wondering where you stand.
                    </p>
                  </div>
                )}

                {/* Tab 2 Content */}
                {activeTab === 2 && (
                  <div className="text-center text-neutral-700 animate-fade-in w-full h-full flex flex-col items-center justify-center p-4">
                    <div className="relative mb-6">
                      <img 
                        src="/materials/you-are-valued-image-four.png" 
                        alt="You are valued - showing healthcare professionals being recognized and respected for their expertise"
                        className="w-52 h-52 md:w-64 md:h-64 lg:w-80 lg:h-80 object-cover rounded-xl drop-shadow-lg"
                      />
                      {/* Floating value indicators for visual appeal */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#9B8FC1] rounded-full flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-white text-xs font-bold">★</span>
                      </div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-[#A3CFA2] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xs font-bold">💎</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-3">Stay in Control</h3>
                    <p className="text-neutral-600 leading-relaxed max-w-xs">
                      Set your terms and let employers compete for your skills. Your time and expertise deserve respect.
                    </p>
                  </div>
                )}

                {/* Tab 3 Content */}
                {activeTab === 3 && (
                  <div className="text-center text-neutral-700 animate-fade-in w-full h-full flex flex-col items-center justify-center p-4">
                    <div className="relative mb-6">
                      <img 
                        src="/materials/shape-the-future-table-imag.png" 
                        alt="Shape the future - showing healthcare professionals collaborating and innovating together"
                        className="w-52 h-52 md:w-64 md:h-64 lg:w-80 lg:h-80 object-contain rounded-xl drop-shadow-lg"
                      />
                      {/* Floating innovation indicators for visual appeal */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#A3CFA2] rounded-full flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-white text-xs font-bold">💡</span>
                      </div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-[#9B8FC1] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xs font-bold">🚀</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-3">Shape the Future</h3>
                    <p className="text-neutral-600 leading-relaxed max-w-xs">
                      Join a community that's changing healthcare recruitment. Your feedback drives our innovation.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Navigation Arrows */}
              <button 
                onClick={() => setActiveTab(activeTab === 1 ? 3 : activeTab - 1)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-neutral-600 hover:text-neutral-900 hover:shadow-xl transition-all duration-300 border border-neutral-200"
                aria-label="Previous feature"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                onClick={() => setActiveTab(activeTab === 3 ? 1 : activeTab + 1)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-neutral-600 hover:text-neutral-900 hover:shadow-xl transition-all duration-300 border border-neutral-200"
                aria-label="Next feature"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos (moved down) */}
      <section className="py-8 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-lg font-medium text-neutral-700 mb-4">
            Open To: <strong>Missouri</strong> • <strong>Kansas</strong>
          </p>
          <p className="text-center text-sm text-neutral-500 mb-8">
            No affiliation with these companies. Logos used for regional reference only.
          </p>
          
          <div className="partner-showcase" aria-label="Hospitals and health systems we serve">
            {/* Desktop: Full grid with navigation */}
            <div className="hidden md:flex items-center justify-between">
              <button onClick={() => handlePrevLogo()} className="partner-nav-btn partner-nav-left" aria-label="Previous logo">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="partner-logos-container" ref={logoContainerRef}>
                <div className="partner-logos-grid">
                  <img src="/Logos/01-hca-midwest-health.png" alt="HCA Midwest Health" className="partner-logo" />
                  <img src="/Logos/02-saint-lukes.png" alt="Saint Luke's" className="partner-logo" />
                  <img src="/Logos/03-sunflower-medical-group.png" alt="Sunflower Medical Group" className="partner-logo" />
                  <img src="/Logos/04-childrens-mercy-kansas-city.png" alt="Children's Mercy Kansas City" className="partner-logo" />
                  <img src="/Logos/05-stormont-vail-health.png" alt="Stormont Vail Health" className="partner-logo" />
                  <img src="/Logos/06-saint-joseph-hospital.png" alt="Saint Joseph Hospital" className="partner-logo" />
                  <img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="Clay Platte Family Medicine Clinic" className="partner-logo" />
                  <img src="/Logos/08-liberty-hospital.png" alt="Liberty Hospital" className="partner-logo" />
                  <img src="/Logos/09-kindred-hospital.png" alt="Kindred Hospital" className="partner-logo" />
                  <img src="/Logos/10-providence-medical-center.png" alt="Providence Medical Center" className="partner-logo" />
                </div>
              </div>
              
              <button onClick={() => handleNextLogo()} className="partner-nav-btn partner-nav-right" aria-label="Next logo">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Mobile: Carousel with visible navigation */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-4">
                <button 
                  onClick={() => setCurrentLogoIndex(prev => prev === 0 ? 9 : prev - 1)} 
                  className="partner-nav-btn partner-nav-left" 
                  aria-label="Previous logo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="text-sm text-neutral-600 font-medium">
                  {currentLogoIndex + 1} of 10
                </div>
                
                <button 
                  onClick={() => setCurrentLogoIndex(prev => prev === 9 ? 0 : prev + 1)} 
                  className="partner-nav-btn partner-nav-right" 
                  aria-label="Next logo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              <div className="partner-logos-container-mobile">
                <div className="partner-logos-carousel" style={{ transform: `translateX(-${currentLogoIndex * 100}%)` }}>
                  <img src="/Logos/01-hca-midwest-health.png" alt="HCA Midwest Health" className="partner-logo-mobile" />
                  <img src="/Logos/02-saint-lukes.png" alt="Saint Luke's" className="partner-logo-mobile" />
                  <img src="/Logos/03-sunflower-medical-group.png" alt="Sunflower Medical Group" className="partner-logo-mobile" />
                  <img src="/Logos/04-childrens-mercy-kansas-city.png" alt="Children's Mercy Kansas City" className="partner-logo-mobile" />
                  <img src="/Logos/05-stormont-vail-health.png" alt="Stormont Vail Health" className="partner-logo-mobile" />
                  <img src="/Logos/06-saint-joseph-hospital.png" alt="Saint Joseph Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="Clay Platte Family Medicine Clinic" className="partner-logo-mobile" />
                  <img src="/Logos/08-liberty-hospital.png" alt="Liberty Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/09-kindred-hospital.png" alt="Kindred Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/10-providence-medical-center.png" alt="Providence Medical Center" className="partner-logo-mobile" />
                </div>
              </div>
              
              {/* Mobile dot indicators */}
              <div className="flex justify-center mt-4 space-x-2">
                {Array.from({ length: 10 }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentLogoIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentLogoIndex 
                        ? 'bg-[#A3CFA2] w-4' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to logo ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative min-h-[60vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-20 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #FAF6F0 0%, #F0F8F0 50%, #FFFFFF 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="text-center">
            {/* Header */}
            <div className="mb-3">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-neutral-900">
                Ready to Take Control of Your Career?
              </h2>
            </div>

            {/* Subheader */}
            <div className="mb-8">
              <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-neutral-600 leading-relaxed max-w-4xl mx-auto">
                Join thousands of healthcare professionals who've found their dream jobs through Nomera Health.
              </p>
            </div>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                disabled
                className="bg-[#A3CFA2]/40 text-white/80 font-bold text-lg px-8 py-4 shadow-none pointer-events-none select-none"
              >
                Coming soon
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                disabled
                className="border-2 border-neutral-200 text-neutral-400 font-semibold text-lg px-8 py-4 pointer-events-none select-none"
              >
                Coming soon
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Partner showcase styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        
        .partner-showcase {
          position: relative;
          padding: 1rem 0;
        }
        
        .partner-logos-container {
          flex: 1;
          overflow: hidden;
          margin: 0 2rem;
        }
        
        .partner-logos-grid {
          display: grid;
          grid-template-columns: repeat(10, 1fr);
          gap: 1.5rem;
          align-items: center;
          justify-items: center;
        }
        
        .partner-nav-btn {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          width: 3rem;
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #6B7280;
          flex-shrink: 0;
        }
        
        .partner-nav-btn:hover {
          background: white;
          border-color: rgba(0, 0, 0, 0.2);
          transform: scale(1.05);
          color: #374151;
        }
        
        .partner-logo {
          height: clamp(48px, 12vw, 120px);
          width: auto;
          opacity: 1;
          filter: none;
          transition: transform .2s ease;
          object-fit: contain;
          min-width: clamp(48px, 12vw, 120px);
          display: block;
        }
        
        .partner-logo:hover {
          transform: translateY(-2px);
        }
        
        @media (max-width: 768px) {
          .partner-logos-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 1rem;
          }
          .partner-nav-btn {
            width: 2.5rem;
            height: 2.5rem;
          }
          .partner-logos-container {
            margin: 0 1rem;
          }
          .partner-logo {
            height: clamp(56px, 15vw, 80px);
            min-width: clamp(56px, 15vw, 80px);
          }
        }
        
        @media (max-width: 480px) {
          .partner-logos-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.75rem;
          }
          .partner-nav-btn {
            width: 2rem;
            height: 2rem;
          }
          .partner-logos-container {
            margin: 0 0.5rem;
          }
          .partner-logo {
            height: clamp(64px, 18vw, 88px);
            min-width: clamp(64px, 18vw, 88px);
          }
        }

        /* Mobile carousel styles */
        .partner-logos-container-mobile {
          overflow: hidden;
          position: relative;
          width: 100%;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .partner-logos-carousel {
          display: flex;
          transition: transform 0.3s ease-in-out;
          width: 1000%; /* 10 logos × 100% */
        }

        .partner-logo-mobile {
          flex-shrink: 0;
          width: 100%; /* Each logo takes full container width */
          height: 120px;
          object-fit: contain;
          padding: 0 2rem;
        }

        @media (max-width: 480px) {
          .partner-logos-container-mobile {
            height: 140px;
          }
          .partner-logo-mobile {
            height: 100px;
            padding: 0 1rem;
          }
        }
      `}</style>
    </div>
  );
}
